import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'
import {
  getBirthdayDisplayPromotionConfig,
  getFacebookShareDisplayPromotionConfig,
  type UserManualReviewBonusDisplaySettings,
} from '../services/promotionConfigService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernContactPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

type ContactTopic = 'birthday-bonus' | 'facebook-share-bonus'

const TOPIC_HELP_CONTENT: Record<
  ContactTopic,
  {
    promotionType: 'birthday_display' | 'facebook_share_display'
    title: string
    subtitle: string
    sectionTitle: string
    sectionSubtitle: string
    requirements: string[]
  }
> = {
  'birthday-bonus': {
    promotionType: 'birthday_display',
    title: 'ขอรับโบนัสวันเกิด',
    subtitle: 'ติดต่อแอดมินพร้อมส่งหลักฐานเพื่อยืนยันวันเกิดก่อนอนุมัติรับสิทธิ์',
    sectionTitle: 'เอกสารที่ต้องส่งให้แอดมิน',
    sectionSubtitle: 'ส่งข้อมูลให้ครบถ้วนก่อนทัก LINE เพื่อให้แอดมินตรวจสอบและอนุมัติได้รวดเร็วขึ้น',
    requirements: [
      'ส่งรูปบัตรประชาชนที่เห็นชื่อและวันเดือนปีเกิดชัดเจน',
      'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
      'รอแอดมินตรวจสอบความถูกต้องก่อนรับโบนัส',
    ],
  },
  'facebook-share-bonus': {
    promotionType: 'facebook_share_display',
    title: 'ขอรับโบนัสแชร์เฟส',
    subtitle: 'ติดต่อแอดมินพร้อมส่งหลักฐานการแชร์โพสต์เพื่อให้ทีมงานตรวจสอบก่อนอนุมัติ',
    sectionTitle: 'หลักฐานที่ต้องส่งให้แอดมิน',
    sectionSubtitle: 'เตรียมภาพและลิงก์ให้ครบก่อนติดต่อ เพื่อให้ทีมงานตรวจสอบได้ทันที',
    requirements: [
      'แคปภาพโพสต์ Facebook ที่แชร์แล้วให้เห็นชัดเจน',
      'ส่ง URL ของโพสต์ที่แชร์เพื่อให้แอดมินเปิดตรวจสอบได้',
      'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
    ],
  },
}

function normalizeTopicHelpSettings(
  source: Partial<UserManualReviewBonusDisplaySettings> | undefined,
  fallback: { title: string; subtitle: string; sectionTitle: string; sectionSubtitle: string; requirements: string[] }
): { title: string; subtitle: string; sectionTitle: string; sectionSubtitle: string; requirements: string[] } {
  const requirements =
    Array.isArray(source?.requirements) && source.requirements.length > 0
      ? source.requirements.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : fallback.requirements

  return {
    title: typeof source?.pageTitle === 'string' && source.pageTitle.trim() ? source.pageTitle : fallback.title,
    subtitle:
      typeof source?.pageSubtitle === 'string' && source.pageSubtitle.trim() ? source.pageSubtitle : fallback.subtitle,
    sectionTitle:
      typeof source?.contactSectionTitle === 'string' && source.contactSectionTitle.trim()
        ? source.contactSectionTitle
        : fallback.sectionTitle,
    sectionSubtitle:
      typeof source?.contactSectionSubtitle === 'string' && source.contactSectionSubtitle.trim()
        ? source.contactSectionSubtitle
        : fallback.sectionSubtitle,
    requirements,
  }
}

/** หน้าติดต่อเรา — ตรง contact.html: หัวข้อ, ไอคอน LINE, LINE ID, ข้อความ 24 ชม. */
const ModernContactPage: React.FC<ModernContactPageProps> = ({ themeConfig, assetConfig }) => {
  const [searchParams] = useSearchParams()
  const [topicHelpOverride, setTopicHelpOverride] = useState<{
    title: string
    subtitle: string
    sectionTitle: string
    sectionSubtitle: string
    requirements: string[]
  } | null>(null)
  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const lineId = themeConfig.settings.contactInfo?.line ?? '@hauytoday'
  const lineHref = lineId.startsWith('http') ? lineId : `https://line.me/ti/p/${lineId.replace('@', '')}`
  const topic = searchParams.get('topic') as ContactTopic | null
  const topicHelpBase = topic && topic in TOPIC_HELP_CONTENT ? TOPIC_HELP_CONTENT[topic] : null

  useEffect(() => {
    let cancelled = false
    if (!topicHelpBase) {
      setTopicHelpOverride(null)
      return
    }

    const loadTopicContent = async () => {
      try {
        const response =
          topicHelpBase.promotionType === 'birthday_display'
            ? await getBirthdayDisplayPromotionConfig(themeConfig.tenantId)
            : await getFacebookShareDisplayPromotionConfig(themeConfig.tenantId)
        if (cancelled) return
        setTopicHelpOverride(normalizeTopicHelpSettings(response?.data?.settings, topicHelpBase))
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load topic help config', error)
          setTopicHelpOverride(normalizeTopicHelpSettings(undefined, topicHelpBase))
        }
      }
    }

    void loadTopicContent()
    return () => {
      cancelled = true
    }
  }, [themeConfig.tenantId, topicHelpBase])

  const topicHelp = topicHelpBase ? topicHelpOverride || normalizeTopicHelpSettings(undefined, topicHelpBase) : null

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">ติดต่อเรา</h3>
            </div>

            <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3">
              {topicHelp ? (
                <div
                  className="border_10 p-3 p-md-4 mb-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(7, 18, 41, 0.96), rgba(17, 53, 95, 0.92))',
                    border: '1px solid rgba(56, 189, 248, 0.35)',
                    boxShadow: '0 16px 40px rgba(2, 6, 23, 0.28)',
                  }}
                >
                  <div className="row align-items-center mb-3">
                    <div className="col-12 col-md-8 mb-3 mb-md-0">
                      <div
                        className="small text-uppercase mb-2"
                        style={{ color: '#7dd3fc', letterSpacing: '0.14em', fontWeight: 700 }}
                      >
                        {topicHelp.sectionTitle}
                      </div>
                      <h4 className="font_md mb-2" style={{ color: '#f8fafc' }}>
                        {topicHelp.title}
                      </h4>
                      <p className="mb-0" style={{ color: 'rgba(226, 232, 240, 0.88)' }}>
                        {topicHelp.subtitle}
                      </p>
                    </div>
                    <div className="col-12 col-md-4">
                      <div
                        className="border_10 p-3 text-center"
                        style={{
                          background: 'rgba(15, 23, 42, 0.5)',
                          border: '1px solid rgba(125, 211, 252, 0.22)',
                        }}
                      >
                        <div
                          className="small text-uppercase mb-2"
                          style={{ color: '#38bdf8', letterSpacing: '0.12em', fontWeight: 700 }}
                        >
                          Bonus Guide
                        </div>
                        <div style={{ color: '#f8fafc', fontWeight: 700 }}>
                          {topic === 'birthday-bonus' ? 'Birthday Bonus' : 'Facebook Share Bonus'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="border_10 p-3 p-md-4"
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(148, 163, 184, 0.24)',
                    }}
                  >
                    <p className="mb-3" style={{ color: 'rgba(226, 232, 240, 0.78)' }}>
                      {topicHelp.sectionSubtitle}
                    </p>
                    <div className="row">
                      {topicHelp.requirements.map((requirement, index) => (
                        <div key={requirement} className="col-12 col-md-4 mb-3 mb-md-0">
                          <div
                            className="border_10 h-100 p-3"
                            style={{
                              background: 'rgba(15, 23, 42, 0.55)',
                              border: '1px solid rgba(125, 211, 252, 0.18)',
                            }}
                          >
                            <div
                              className="small text-uppercase mb-2"
                              style={{ color: '#7dd3fc', letterSpacing: '0.08em', fontWeight: 700 }}
                            >
                              Step {index + 1}
                            </div>
                            <div style={{ color: '#f8fafc', lineHeight: 1.6 }}>{requirement}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              <a href={lineHref} target="_blank" rel="noopener noreferrer" className="line_ct mx-auto d-block text-center">
                <img src={getAssetUrl('/img/LINE_logo.svg.png')} alt="LINE" onError={(e) => { (e.target as HTMLImageElement).src = getAssetUrl('/img/line_icon.png') }} />
              </a>

              <div className="c_tertiary text-center mt-4">
                <h2 className="">
                  <span className="bg_secondary c_primary border_5 font_md px-2">
                    LINE ID : {lineId}
                  </span>
                </h2>

                <h4 className="c_quinary mt-2">**(มี@ด้านหน้า)**</h4>

                <h3 className="mt-2 font_md">
                  หรือ กดที่ ICON LINE ได้เลย
                  <br />
                  เรามีพนักงานรอต้อนรับลูกค้าตลอด 24 ชั่วโมง
                </h3>
                <h4 className="mt-3 c_quinary">
                  ติดต่อให้ความช่วยเหลือ ตอบคำถาม <br />
                  แก้ไขปัญหาตลอด 24 ชั่วโมง
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernContactPage
